import { PayloadedAction } from '../../shared/types'
import {
  RESET_PASSENGERS,
  SET_CONTACT_INFO,
  SET_CONTACT_NUMBER,
} from './submit.actions'
import { Option } from '../ui/ui.types'
import { CANCEL_EDITING_REQUEST } from '../../shared/actions/shared.actions'

export interface Passengers {
  quantity: Option
  notes?: string
}

export interface UserContactInfo {
  contactNumber: string
  firstName: string
  lastName: string
  shouldTextUpdates?: boolean
  displayId?: boolean
  additionalDetails: string
}

export interface SubmitState {
  contactInfo: UserContactInfo
  passengers: Passengers
}

export const initialState: SubmitState = createInitialState()

function createInitialState(): SubmitState {
  return {
    contactInfo: {
      contactNumber: '',
      firstName: '',
      lastName: '',
      shouldTextUpdates: true,
      displayId: false,
      additionalDetails: '',
    },
    passengers: {
      quantity: null,
      notes: '',
    },
  }
}

export const passengerOptions = (): Option[] => [
  { label: '0' },
  { label: '1' },
  { label: '2' },
  { label: $localize`3 OR MORE`, value: '3 OR MORE' },
]

/**
 * This slice is persisted to sessionStorage. Before the ngrx-forms migration it
 * held `FormGroupState` objects, so a session that started on an older build can
 * rehydrate `{ id, value, controls, ... }` where a plain value is now expected —
 * which would leave the fields silently blank rather than throwing. Version
 * changes are already handled by `state-version.utils`; this covers a session
 * that spans a same-version deploy.
 */
const isLegacyFormState = (value: unknown): boolean =>
  Boolean(value) &&
  typeof value === 'object' &&
  ('controls' in (value as object) || 'id' in (value as object))

export function reducer(
  state: SubmitState | undefined = initialState,
  action: PayloadedAction
): SubmitState {
  if (isLegacyFormState(state.contactInfo) || isLegacyFormState(state.passengers)) {
    state = createInitialState()
  }

  switch (action.type) {
    case SET_CONTACT_INFO:
      return {
        ...state,
        contactInfo: action.payload,
      }
    case RESET_PASSENGERS:
      return {
        ...state,
        passengers: {
          quantity: null,
          notes: '',
        },
      }
    case SET_CONTACT_NUMBER:
      return {
        ...state,
        contactInfo: {
          ...state.contactInfo,
          contactNumber: action.payload,
        },
      }
    case CANCEL_EDITING_REQUEST:
      // Allowing for a new initial state prevents corruption of the original initialState value
      return createInitialState()
    default:
      return state
  }
}
